import React, { MutableRefObject, useImperativeHandle } from 'react';
import { FormProvider } from 'react-hook-form';
import { Stack, Button } from '@mui/material';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { EquipmentFormValue } from '../../../types';
import { LoadingFallback } from '../LoadingFollback';
import { prepareCreateUpdateEquipmentDTO } from '../../../lib';
import { useEquipmentForm } from '../../../hooks/useEquipmentForm';
import { Attachments, CommonInfo, Pricing, Totals } from './sections';
import { CreateEquipmentVariables, UpdateEquipmentVariables } from '../../../api/equipment/types';

interface Props {
  equipmentId: string | undefined;
  onCancel?: VoidFunction;
  submitRef?: MutableRefObject<{ submit: () => EquipmentFormValue } | null>;
  onSubmit?: (data: {
    form: EquipmentFormValue;
    createDTO: Omit<CreateEquipmentVariables['input'], 'dynamicsVendorOpportunityId' | 'vendorOpportunityId'>;
    updateDTO: Omit<UpdateEquipmentVariables['input'], 'dynamicsVendorOpportunityId' | 'vendorOpportunityId'>;
  }) => void;
}

export const EquipmentForm = ({ equipmentId, submitRef, onSubmit, onCancel }: Props) => {
  const { form, isLoading } = useEquipmentForm({ id: equipmentId });

  useImperativeHandle(submitRef, () => ({
    submit: () => form.getValues(),
  }));

  return (
    <RenderWithFallback isLoading={isLoading} fallBack={<LoadingFallback />}>
      <FormProvider {...form}>
        <Stack gap="1.5rem" padding="1rem">
          <CommonInfo />
          <Pricing />
          <Attachments />
          <Totals />

          {!!onSubmit && (
            <Stack direction="row" gap="0.5rem" justifyContent="flex-end">
              {!!onCancel && (
                <Button variant="outlined" onClick={onCancel}>
                  Cancel
                </Button>
              )}

              <Button
                variant="contained"
                onClick={form.handleSubmit(data => {
                  const dto = prepareCreateUpdateEquipmentDTO(data);
                  onSubmit({ form: data, createDTO: dto, updateDTO: { ...dto, equipmentId: equipmentId! } });
                })}
              >
                Save
              </Button>
            </Stack>
          )}
        </Stack>
      </FormProvider>
    </RenderWithFallback>
  );
};
